<template>
  <main class="about-content-page">
    <div class="about-content-page__wrapper">
      <div class="about-content-page__aside">
        <AboutMenu v-if="isShowMenu" :active-slug="slug" :items="ABOUT_MENU_ITEMS" />
      </div>
      <article class="about-content-page__article">
        <AboutContentSkeleton v-if="isPending" />
        <template v-else>
          <h1 class="about-content-page__title">{{ content.title }}</h1>
          <div class="about-content-page__html" v-html="content.html" />
        </template>
      </article>
    </div>
  </main>
</template>

<script setup lang="ts">
import { useSeoMeta } from '@unhead/vue';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { ABOUT_MENU_ITEMS } from '@/entities/about/model/constants';
import { useAboutContentPageQuery } from '@/entities/about/model/useAboutContentPageQuery';
import { useAnalytics } from '@/shared/lib/analytics';
import { useRenderReport } from '@/shared/lib/lifecycle';
import AboutContentSkeleton from '@/widgets/about-content/ui/AboutContentSkeleton.vue';
import AboutMenu from '@/widgets/about-menu/ui/AboutMenu.vue';

const route = useRoute();
const { trackEvent } = useAnalytics();

useRenderReport();
const slug = computed(() => {
  const routeSlug = route.params.slug;

  return (Array.isArray(routeSlug) ? routeSlug[0] : routeSlug) ?? '';
});
const { data, isPending } = useAboutContentPageQuery(slug);

useSeoMeta({
  title: computed(() => data.value?.seo.title ?? ''),
  description: computed(() => data.value?.seo.description ?? ''),
  ogTitle: computed(() => data.value?.seo.ogTitle ?? ''),
  ogDescription: computed(() => data.value?.seo.ogDescription ?? ''),
});

const COOKIE_SLUG = 'cookie';
const COOKIE_PAGEVIEW_EVENT = 'cookie_about.pageview';

const isCookiePageViewTracked = ref(false);

watch(
  slug,
  (nextSlug) => {
    if (nextSlug !== COOKIE_SLUG) {
      isCookiePageViewTracked.value = false;

      return;
    }

    if (isCookiePageViewTracked.value) {
      return;
    }

    isCookiePageViewTracked.value = true;
    void trackEvent(COOKIE_PAGEVIEW_EVENT, {});
  },
  { immediate: true },
);

const hasActiveMenuItem = computed(() =>
  ABOUT_MENU_ITEMS.some((item) => item.type === 'content-page' && !item.hidden && item.id === slug.value),
);
const isShowMenu = computed(() => {
  return hasActiveMenuItem.value;
});
const content = computed(() => ({
  title: data.value?.title ?? '',
  html: data.value?.html ?? '',
}));
</script>

<style scoped lang="scss">
.about-content-page {
  color: var(--alias-color-text-default);
  background: var(--alias-color-background-default);
}

.about-content-page__wrapper {
  display: flex;
  gap: 128px;
  width: min(100%, 1220px);
  padding: 0 var(--alias-space-20);
  margin: 0 auto;

  @include adaptivity.tablet {
    flex-direction: column;
    gap: 0;
  }
}

.about-content-page__aside {
  width: 288px;
  margin-top: var(--alias-space-56);
  margin-bottom: var(--alias-space-56);

  @include adaptivity.tablet {
    position: sticky;
    top: 0;
    z-index: 1;
    width: 100%;
    margin-top: 0;
    margin-bottom: 0;
  }
}

.about-content-page__article {
  flex: 1;
  min-width: 0;
}

.about-content-page__title {
  margin: var(--alias-space-56) 0 0;

  @include typography.base-1000-950-extra;

  @include adaptivity.tablet {
    margin-top: var(--alias-space-24);

    @include typography.base-800-750-extra;
  }
}

.about-content-page__html {
  margin-top: var(--alias-space-16);
  margin-bottom: var(--alias-space-56);

  @include typography.base-400-400-regular;
}

.about-content-page__html :deep(h1) {
  margin: var(--alias-space-56) 0 0;

  @include adaptivity.tablet {
    margin-top: var(--alias-space-24);
  }
}

.about-content-page__html :deep(h1 + h2) {
  margin-top: var(--alias-space-48);

  @include adaptivity.mobile {
    margin-top: var(--alias-space-40);
  }
}

.about-content-page__html :deep(h1 + img) {
  margin-top: var(--alias-space-48);
}

.about-content-page__html :deep(h1 + h3),
.about-content-page__html :deep(h1 + h4),
.about-content-page__html :deep(h1 + ul),
.about-content-page__html :deep(h1 + ol) {
  margin-top: var(--alias-space-24);
}

.about-content-page__html :deep(h2) {
  margin: var(--alias-space-48) 0 0;

  @include typography.base-800-750-extra;

  @include adaptivity.tablet {
    @include typography.base-700-500-extra;
  }

  @include adaptivity.mobile {
    margin-top: var(--alias-space-40);
  }
}

.about-content-page__html :deep(h2 + h3),
.about-content-page__html :deep(h2 + img) {
  margin-top: var(--alias-space-24);
}

.about-content-page__html :deep(h2 + h4),
.about-content-page__html :deep(h2 + p),
.about-content-page__html :deep(h2 + div),
.about-content-page__html :deep(h2 + div p:first-of-type),
.about-content-page__html :deep(h2 + ul),
.about-content-page__html :deep(h2 + ol),
.about-content-page__html :deep(h2 + span) {
  margin-top: var(--alias-space-24);

  @include adaptivity.mobile {
    margin-top: var(--alias-space-16);
  }
}

.about-content-page__html :deep(h3) {
  margin: var(--alias-space-24) 0 0;

  @include typography.base-600-500-extra;
}

.about-content-page__html :deep(h3 + h4),
.about-content-page__html :deep(h3 + p),
.about-content-page__html :deep(h3 + div),
.about-content-page__html :deep(h3 + span),
.about-content-page__html :deep(h3 + ul),
.about-content-page__html :deep(h3 + ol) {
  margin-top: var(--alias-space-8);
}

.about-content-page__html :deep(h3 + img) {
  margin-top: var(--alias-space-24);

  @include adaptivity.mobile {
    margin-top: var(--alias-space-16);
  }
}

.about-content-page__html :deep(h4) {
  margin: var(--alias-space-24) 0 0;

  @include typography.base-400-400-semi;
}

.about-content-page__html :deep(h4 + p),
.about-content-page__html :deep(h4 + div),
.about-content-page__html :deep(h4 + span),
.about-content-page__html :deep(h4 + ul),
.about-content-page__html :deep(h4 + div p:first-of-type),
.about-content-page__html :deep(h4 + ol) {
  margin-top: 0 !important;
}

.about-content-page__html :deep(h4 + img) {
  margin-top: var(--alias-space-24);

  @include adaptivity.mobile {
    margin-top: var(--alias-space-8);
  }
}

.about-content-page__html :deep(img) {
  width: 100%;
  max-width: 100%;
}

.about-content-page__html :deep(img + img),
.about-content-page__html :deep(p + img),
.about-content-page__html :deep(em + img),
.about-content-page__html :deep(a + img),
.about-content-page__html :deep(br + img),
.about-content-page__html :deep(span + img) {
  margin-top: var(--alias-space-24);
}

.about-content-page__html :deep(p) {
  margin: var(--alias-space-24) 0 0;

  @include typography.base-400-400-regular;
}

.about-content-page__html :deep(img + p) {
  margin-top: var(--alias-space-8);
}

.about-content-page__html :deep(ul),
.about-content-page__html :deep(ol) {
  padding-left: 18px;
  margin: var(--alias-space-24) 0 0;

  @include typography.base-400-400-regular;
}

.about-content-page__html :deep(ul + ul) {
  margin-top: var(--alias-space-12);
}

.about-content-page__html :deep(li:not(:first-child)) {
  margin: var(--alias-space-12) 0;

  @include typography.base-400-400-regular;
}

.about-content-page__html :deep(a) {
  color: var(--alias-color-text-default);
  text-decoration-color: var(--alias-color-text-subtler);
  text-underline-offset: var(--alias-space-4);
}

.about-content-page__html :deep(a):hover {
  color: var(--alias-color-link-darked-hovered);
  text-decoration-color: var(--alias-color-link-darked-hovered);
}
</style>
